import { ContentCopy, FilterList, PermMedia } from "@mui/icons-material"
import { Box, Divider, Pagination, Popover } from "@mui/material"
import React from "react"
import { useSelector } from "react-redux"
import useLocalStorage from "use-local-storage"
import AlertDialog from "../../components/AlertDialog"
import CustomList from "../../components/CustomList"
import { dialogModes, dialogTypes } from "../../constants"
import useDialogProps from "../../hooks/useDialogProps"
import useMenu from "../../hooks/useMenu"
import { useEmployees, useGroups, useStudentsByGroup, useStudios, useSubjects } from "../../redux/dicts/hooks"
import { useFilesForLessonMaterial } from "../../redux/filesSlice"
import { useLessonMaterial } from "../../redux/lessonMaterialSlice"
import { useLessons } from "../../redux/lessonsSlice"
import { useLogBookItems } from "../../redux/logBookItemsSlice"
import { useTasksByLesson } from "../../redux/taskSlice"
import CopyDialog from "./components/CopyLessonsDialog"
import EmployeeLogBook from "./components/EmployeeLogBook"
import LessonAnswerDialog from "./components/LessonAnswerDialog"
import LessonFilters from "./components/LessonFilters"
import LessonForm from "./components/LessonForm"
import LessonMaterialForm from "./components/LessonMaterialForm"
import LessonTaskForm from "./components/LessonTaskForm"
import useLessonActionHandlers from "./hooks/useLessonActionHandlers"


const LessonLoogbook = () => {

    const userInfo = useSelector(x => x.user.userInfo)

    const [filters, setFilters] = useLocalStorage('eschool_el_filters', {
        subject: null,
        group: null,
        dateBefore: "",
        dateAfter: "",
        timeBefore: "",
        timeAfter: "",
        mainGroup: null,
        studyYear: null,
    })

    const {
        lessons, refetchLessons,
        currentLesson,
        lessonsIsLoading,
        lessonsPage, setLessonsPage,
        lessonsPageSize,
        lessonsTotal
    } = useLessons(
        userInfo?.role?.id, "employee",
        filters.group, filters.subject,
        filters.dateBefore, filters.dateAfter,
        filters.timeBefore, filters.timeAfter,
        filters.mainGroup, filters.studyYear
    )

    const { logBook, logBookIsLoading } = useLogBookItems(currentLesson)

    const { subjectOptions, subjectLookups, subjectsIsLoading } = useSubjects()

    const { groupOptions, groupLookups, groupsIsLoading } = useGroups()

    const { employeeOptions, employeeLookups } = useEmployees()

    const { dialogData, dialogMode, dialogType, dialogIsOpen, openDialog, closeDialog } = useDialogProps()

    const { students, studentsIsLoading } = useStudentsByGroup(currentLesson?.group)

    const { studioOptions, studioLookups } = useStudios()

    const { tasks, tasksIsLoading } = useTasksByLesson(currentLesson)

    const { lessonMaterial } = useLessonMaterial(dialogType === dialogTypes.LESSON_MATERIAL ? dialogData : null)

    const {
        acceptedFiles, savedFiles,
        handleAddFiles, handleResetSavedFiles, handleRemoveAcceptedFile,
    } = useFilesForLessonMaterial()
    
    const [checked, setChecked] = React.useState([])

    const {
        handleSetCurrentLesson,
        handleCreateLessonIntent, handleCreateLesson,
        handleEditLessonIntent, handleEditLesson,
        handleDeleteLessonIntent, handleDeleteLesson,
        handleUpdateLogBook,
        handleOpenTaskForm, handleEditTask, handleOpenTaskAnswerDialog,
        handleEditLessonMaterialIntent, handleCreateLessonMaterial, handleUpdateLessonMaterial,
        handleCopyIntent, handleCopy
    } = useLessonActionHandlers(
        openDialog, closeDialog, dialogData,
        savedFiles,
        handleResetSavedFiles,
        userInfo,
        refetchLessons,
        checked, setChecked
    )

    const editDialogIsOpen = dialogIsOpen && dialogType === dialogTypes.LESSON && dialogMode !== dialogModes.DELETE
    const deleteDialogIsOpen = dialogIsOpen && dialogType === dialogTypes.LESSON && dialogMode === dialogModes.DELETE

    const editTaskDialogIsOpen = dialogIsOpen && dialogType === dialogTypes.TASK && dialogMode === dialogModes.UPDATE
    const answerDialogIsOpen = dialogIsOpen && dialogType === dialogTypes.TASK && dialogMode === dialogModes.VIEW

    const editLessonMaterialDialogIsOpen = dialogIsOpen && dialogType === dialogTypes.LESSON_MATERIAL
    const copyDialogIsOpen = dialogIsOpen && dialogType === dialogTypes.LESSON_COPY

    const { anchorEl, open, handleDefaultMenuClick, handleMenuClose } = useMenu()

    return <Box sx={{ minWidth: 1200, width: "100%", height: "100%", display: "flex", overflowX: "hidden" }}>

        <Popover open={open} onClose={handleMenuClose} anchorEl={anchorEl}>
            <LessonFilters
                initialValues={filters}
                onSubmit={setFilters}
                subjectOptions={subjectOptions}
                subjectLookups={subjectLookups}
                groupOptions={groupOptions}
                groupLookups={groupLookups}
                onClose={handleMenuClose}
            />
        </Popover>

        <Box sx={{ width: 400, height: "100%" }}>
            <CustomList
                data={lessons}
                current={currentLesson}
                onSelect={handleSetCurrentLesson}
                isLoading={lessonsIsLoading || groupsIsLoading || subjectsIsLoading}
                onAdd={handleCreateLessonIntent}
                onEdit={handleEditLessonIntent}
                onDelete={handleDeleteLessonIntent}
                onBulkDelete={handleDeleteLessonIntent}
                width={400}
                height="calc(100% - 50px)"
                primaryValueGetter={x => `${subjectLookups[x.subject]?.name} - ${groupLookups[x.group]?.name}`}
                secondaryValueGetter={x => `${new Date(x.date).toLocaleString().slice(0, 17)} - ${new Date(new Date(x.date).getTime() + 60000 * x.duration).toLocaleTimeString().slice(0, 5)}`}
                additionalActions={[{
                    icon: PermMedia,
                    onClick: handleEditLessonMaterialIntent
                }]}
                freeActions={[
                    {
                        icon: FilterList,
                        onClick: handleDefaultMenuClick,
                    },
                    {
                        icon: ContentCopy,
                        onClick: handleCopyIntent
                    }
                ]}
                enableChecking
                checked={checked}
                setChecked={setChecked}
            />
            <Divider orientation="horizontal" />
            <Pagination
                sx={{ p: 1 }}
                shape="rounded"
                count={Math.ceil(lessonsTotal / lessonsPageSize)}
                variant="outlined"
                page={lessonsPage}
                onChange={(_, page) => {
                    setChecked([])
                    setLessonsPage(page)
                }}
            />
        </Box>

        <Divider orientation="vertical" />

        <Box sx={{ width: "calc(100% - 401px)" }}>
            <EmployeeLogBook
                currentLesson={currentLesson}
                logBook={logBook}
                tasks={tasks}
                isLoading={logBookIsLoading || studentsIsLoading || tasksIsLoading}
                students={students}
                onUpdateLogBook={handleUpdateLogBook}
                onOpenTaskForm={handleOpenTaskForm}
                onEditTask={handleEditTask}
                onViewAnswer={handleOpenTaskAnswerDialog}
            />
        </Box>

        <LessonForm
            open={editDialogIsOpen}
            onClose={closeDialog}
            data={dialogData}
            subjectOptions={subjectOptions}
            subjectLookups={subjectLookups}
            employeeOptions={employeeOptions}
            employeeLookups={employeeLookups}
            groupOptions={groupOptions}
            groupLookups={groupLookups}
            studioOptions={studioOptions}
            studioLookups={studioLookups}
            onSave={values => dialogMode === dialogModes.CREATE ? handleCreateLesson(values) : handleEditLesson(values)}
            dialogMode={dialogMode}
        />

        <LessonTaskForm
            open={editTaskDialogIsOpen}
            onSave={handleEditTask}
            onClose={closeDialog}
            data={dialogData}
        />

        <LessonMaterialForm
            open={editLessonMaterialDialogIsOpen}
            onClose={() => {
                closeDialog()
                handleResetSavedFiles()
            }}
            data={lessonMaterial}
            currentLesson={dialogData}
            acceptedFiles={acceptedFiles}
            onAddFiles={handleAddFiles}
            onRemoveAcceptedFile={handleRemoveAcceptedFile}
            onSave={lessonMaterial?.id ? handleUpdateLessonMaterial : handleCreateLessonMaterial}
        />

        <LessonAnswerDialog
            open={answerDialogIsOpen}
            data={dialogData}
            onClose={closeDialog}
        />

        <AlertDialog
            open={deleteDialogIsOpen}
            onCancel={closeDialog}
            onOk={handleDeleteLesson}
            title={`Удаление заняти${dialogData ? "я" : "й"}`}
            description={dialogData ? "Вы уверены, что хотите удалить занятие?" : "Вы уверены, что хотите удалить выбранные занятия?"}
        />

        <CopyDialog
            open={copyDialogIsOpen}
            onClose={closeDialog}
            onSave={handleCopy}
        />

    </Box>
}

export default LessonLoogbook